import type { FetchData } from '@hooks/useDataFetcher';
import { useCallback, useState } from 'react';
import type { IPdPackage, IPdProduct, IPdVariant } from 'types/custom';

interface IState {
  loading: boolean;
  productsAndPackages: {
    loading: boolean;
    list: Array<IPdPackage | IPdProduct>;
  };
  productAndPackageVariants: {
    loading: boolean;
    list: Array<IPdVariant>;
  };
}

export type UseProductDetails = IState & {
  getProductsAndPackages: () => Promise<Array<IPdPackage | IPdProduct>>;
  getProductOrPackageById: (
    id: string,
  ) => Promise<IPdProduct | IPdPackage | undefined>;
  getProductAndPackageVariants: () => Promise<Array<IPdVariant>>;
  createPackageVariant: (body: IPdVariant) => Promise<IPdVariant>;
  createProductVariant: (body: IPdVariant) => Promise<IPdVariant>;
  createProduct: (body: IPdProduct) => Promise<IPdProduct>;
  updateProduct: (id: string, body: IPdProduct) => Promise<IPdProduct>;
  deleteProduct: (id: string) => Promise<void>;
  updateProductVariant: (
    productId: string,
    variantId: string,
    body: IPdVariant,
  ) => Promise<IPdVariant>;
  createPackage: (body: IPdPackage) => Promise<IPdPackage>;
  updatePackage: (id: string, body: IPdPackage) => Promise<IPdPackage>;
  deletePackage: (id: string) => Promise<void>;
  updatePackageVariant: (
    packageId: string,
    variantId: string,
    body: IPdVariant,
  ) => Promise<IPdVariant>;
};

export const useProductDetails = (fetchData: FetchData): UseProductDetails => {
  const [state, setState] = useState<IState>({
    loading: false,
    productsAndPackages: { list: [], loading: false },
    productAndPackageVariants: { list: [], loading: false },
  });

  // helpers

  // [deprecated]
  const createVariant =
    (
      type: 'product' | 'package',
    ):
      | UseProductDetails['createPackageVariant']
      | UseProductDetails['createProductVariant'] =>
    async (body) => {
      setState((state) => ({
        ...state,
        productsAndPackages: { ...state.productsAndPackages, loading: true },
        productAndPackageVariants: {
          ...state.productAndPackageVariants,
          loading: true,
        },
      }));

      const newVariant = await fetchData<IPdVariant>(
        `/api/product-details/v2/${type}s/variants`,
        undefined,
        'POST',
        body,
      );

      setState((state) => ({
        ...state,
        productsAndPackages: {
          ...state.productsAndPackages,
          loading: false,
          list: newVariant.id
            ? state.productsAndPackages.list.map((p) => ({
                ...p,
                variants:
                  p.id === body.id ? [...p.variants, newVariant] : p.variants,
              }))
            : state.productsAndPackages.list,
        },
        productAndPackageVariants: {
          ...state.productAndPackageVariants,
          loading: false,
          list: newVariant.id
            ? [newVariant, ...state.productAndPackageVariants.list].sort(
                (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
              )
            : state.productAndPackageVariants.list,
        },
      }));

      if (!newVariant?.id) {
        throw new Error(
          (newVariant as any)?.message ?? 'An unknown error occurred',
        );
      }

      return newVariant;
    };

  // [deprecated]
  const updateVariant =
    (
      type: 'product' | 'package',
    ):
      | UseProductDetails['updatePackageVariant']
      | UseProductDetails['updateProductVariant'] =>
    async (productId, variantId, body) => {
      setState((state) => ({
        ...state,
        productsAndPackages: { ...state.productsAndPackages, loading: true },
      }));

      const updatedVariant = await fetchData<IPdVariant>(
        `/api/product-details/v2/${type}s/variants/${variantId}`,
        undefined,
        'PUT',
        body,
      );

      setState((state) => ({
        ...state,
        productAndPackageVariants: {
          ...state.productAndPackageVariants,
          list: state.productAndPackageVariants.list.map((v) =>
            v.id === updatedVariant.id ? updatedVariant : v,
          ),
        },
        productsAndPackages: {
          ...state.productsAndPackages,
          loading: false,
          list: updatedVariant.id
            ? state.productsAndPackages.list.map((p) =>
                p.id === productId
                  ? {
                      ...p,
                      variants: p.variants.map((variant) =>
                        variant.id === variantId ? updatedVariant : variant,
                      ),
                    }
                  : p,
              )
            : state.productsAndPackages.list,
        },
      }));

      if (!updatedVariant?.id) {
        throw new Error(
          (updatedVariant as any)?.message ?? 'An unknown error occurred',
        );
      }

      return updatedVariant;
    };

  // Products and packages

  const getProductsAndPackages: UseProductDetails['getProductsAndPackages'] =
    async () => {
      if (state.productsAndPackages.list.length) {
        return state.productsAndPackages.list;
      }

      setState((state) => ({
        ...state,
        productsAndPackages: {
          ...state.productsAndPackages,
          loading: true,
        },
      }));

      const list = await fetchData<Array<IPdProduct | IPdPackage>>(
        '/api/product-details/v1/products-and-packages',
      );

      setState((state) => ({
        ...state,
        productsAndPackages: {
          ...state.productsAndPackages,
          loading: false,
          list: list.length ? list : state.productsAndPackages.list,
        },
      }));

      return list.length ? list : [];
    };

  const getProductOrPackageById = useCallback<
    UseProductDetails['getProductOrPackageById']
  >(
    async (id) => {
      setState((prev) => ({ ...prev, loading: true }));

      const productOrPackage = await fetchData<IPdProduct | IPdPackage>(
        `/api/product-details/v1/products-and-packages/${id}`,
      );

      setState((prev) => ({ ...prev, loading: false }));

      return !productOrPackage.id ? undefined : productOrPackage;
    },
    [fetchData],
  );

  const getProductAndPackageVariants: UseProductDetails['getProductAndPackageVariants'] =
    async () => {
      if (state.productAndPackageVariants.list.length) {
        return state.productAndPackageVariants.list;
      }

      setState((state) => ({
        ...state,
        productAndPackageVariants: {
          ...state.productAndPackageVariants,
          loading: true,
        },
      }));

      const res = await fetchData<IPdVariant[]>(
        '/api/product-details/v1/products-and-packages/variants',
      );

      setState((state) => ({
        ...state,
        productAndPackageVariants: {
          ...state.productAndPackageVariants,
          loading: false,
          list: res.length ? res : state.productAndPackageVariants.list,
        },
      }));

      return res.length ? res : [];
    };

  // Products

  const createProduct: UseProductDetails['createProduct'] = async (body) => {
    setState((state) => ({
      ...state,
      productsAndPackages: { ...state.productsAndPackages, loading: true },
    }));
    const newProduct = await fetchData<IPdProduct>(
      '/api/product-details/v2/products',
      undefined,
      'POST',
      body,
    );
    setState((state) => ({
      ...state,
      productsAndPackages: {
        ...state.productsAndPackages,
        loading: false,
        list: newProduct?.id
          ? [...state.productsAndPackages.list, newProduct]
          : state.productsAndPackages.list,
      },
    }));
    if (!newProduct?.id) {
      throw new Error(
        (newProduct as any)?.message ?? 'An unknown error occurred',
      );
    }
    return newProduct;
  };

  const updateProduct: UseProductDetails['updateProduct'] = async (
    id,
    body,
  ) => {
    setState((state) => ({
      ...state,
      productsAndPackages: { ...state.productsAndPackages, loading: true },
    }));
    const updatedProduct = await fetchData<IPdProduct>(
      `/api/product-details/v2/products/${id}`,
      undefined,
      'PUT',
      body,
    );

    setState((state) => ({
      ...state,
      productsAndPackages: {
        ...state.productsAndPackages,
        loading: false,
        list: state.productsAndPackages.list.map((p) =>
          p.id === updatedProduct?.id ? updatedProduct : p,
        ),
      },
    }));

    if (!updatedProduct?.id) {
      throw new Error(
        (updatedProduct as any)?.message ?? 'An unknown error occurred',
      );
    }

    return updatedProduct;
  };

  const createProductVariant: UseProductDetails['createProductVariant'] =
    createVariant('product');

  const updateProductVariant: UseProductDetails['updateProductVariant'] =
    updateVariant('product');

  const deleteProduct: UseProductDetails['deleteProduct'] = async (id) => {
    setState((state) => ({
      ...state,
      productsAndPackages: { ...state.productsAndPackages, loading: true },
    }));

    await fetchData(
      `/api/product-details/v2/products/${id}`,
      undefined,
      'DELETE',
    );

    setState((state) => ({
      ...state,
      productsAndPackages: {
        ...state.productsAndPackages,
        loading: false,
        list: state.productsAndPackages.list.filter((p) => p.id !== id),
      },
    }));
  };

  // Packages

  const createPackage: UseProductDetails['createPackage'] = async (body) => {
    setState((state) => ({
      ...state,
      productsAndPackages: { ...state.productsAndPackages, loading: true },
    }));
    const newPackage = await fetchData<IPdPackage>(
      '/api/product-details/v2/packages',
      undefined,
      'POST',
      body,
    );
    setState((state) => ({
      ...state,
      productsAndPackages: {
        ...state.productsAndPackages,
        loading: false,
        list: newPackage?.id
          ? [...state.productsAndPackages.list, newPackage]
          : state.productsAndPackages.list,
      },
    }));
    if (!newPackage?.id) {
      throw new Error(
        (newPackage as any)?.message ?? 'An unknown error occurred',
      );
    }
    return newPackage;
  };

  const updatePackage: UseProductDetails['updatePackage'] = async (
    id,
    body,
  ) => {
    setState((state) => ({
      ...state,
      productsAndPackages: { ...state.productsAndPackages, loading: true },
    }));

    const res = await fetchData<IPdPackage>(
      `/api/product-details/v2/packages/${id}`,
      undefined,
      'PUT',
      body,
    );

    setState((state) => ({
      ...state,
      productsAndPackages: {
        ...state.productsAndPackages,
        loading: false,
        list: state.productsAndPackages.list.map((pkg) =>
          pkg.id === res.id ? res : pkg,
        ),
      },
    }));

    if (!res.id) {
      throw new Error((res as any)?.message ?? 'An unknown error occurred');
    }

    return res;
  };

  const createPackageVariant: UseProductDetails['createPackageVariant'] =
    createVariant('package');

  const updatePackageVariant: UseProductDetails['updatePackageVariant'] =
    updateVariant('package');

  // [deprecated]
  const deletePackage: UseProductDetails['deletePackage'] = async (id) => {
    setState((state) => ({
      ...state,
      productsAndPackages: { ...state.productsAndPackages, loading: true },
    }));

    await fetchData(
      `/api/product-details/v2/packages/${id}`,
      undefined,
      'DELETE',
    );

    setState((state) => ({
      ...state,
      productsAndPackages: {
        ...state.productsAndPackages,
        loading: false,
        list: state.productsAndPackages.list.filter((p) => p.id !== id),
      },
    }));
  };

  return {
    deletePackage,
    deleteProduct,
    createPackage,
    updatePackage,
    createProduct,
    updateProduct,
    createProductVariant,
    createPackageVariant,
    updatePackageVariant,
    updateProductVariant,
    getProductsAndPackages,
    getProductOrPackageById,
    getProductAndPackageVariants,
    productsAndPackages: state.productsAndPackages,
    productAndPackageVariants: state.productAndPackageVariants,
    loading:
      state.loading ||
      state.productsAndPackages.loading ||
      state.productAndPackageVariants.loading,
  };
};
